// Layout shell for the Document knowledge Enrichment.
import React from "react";
import { FileText } from "lucide-react";
import {
  getDomainRuleValidation,
  type DomainClassifyResult,
  type ConceptClassifyResult,
  type RequirementClassifyResult,
  type IngestStageEvent,
  type ValidateFileResult,
} from "./api/contentRegistryApi";
import type {
  LayerKey,
  LayerRunState,
} from "../../../stores/processingJobStore";
import {
  DomainIntelligenceDrawer,
  DOMAIN_RULE_VALIDATION_LAYER,
  DOMAIN_RULE_VALIDATION_OUTCOME,
  DOMAIN_RULE_VALIDATION_SCOPE_KEY,
  ruleFindings,
  toRuleValidationList,
} from "./Domain";
import { ConceptIntelligenceDrawer } from "./Concept";
import { RequirementIntelligenceDrawer } from "./Requirement";
import {
  KnowledgeQualityDrawer,
  type KnowledgeQualityResult,
} from "./KnowledgeQuality";
import { ConsistencyDrawer, type ConsistencyResult } from "./Consistency";
import { GovernanceDrawer, type GovernanceResult } from "./Governance";
import {
  RelationshipIntelligenceDrawer,
  type RelationshipResult,
} from "./Relationship";
import {
  HOLD_STAGE_INDEX,
  PIPELINE_STAGES,
  VALIDATION_LAYERS,
  type ConsolePhase,
  type PipelineRun,
} from "./processingPipeline";
import {
  ExtractionFeed,
  Panel,
  ConsoleKeyframes,
} from "./ConsoleParts";
import {
  DOC_TYPE_COLORS,
  JOB_PHASE,
  LAYER_RUN_STATUS,
  STATUS_TONE,
  YIELD_TONE_CLASS,
} from "./constants";
import {
  buildYield,
  buildStageStates,
  buildStageStatesFromEvents,
  formatDocType,
  type ConsoleRuleCounts,
  type ConsoleStageState,
} from "./utils";
import { ConsolePipelineStrip } from "./ConsolePipelineStrip";
import { ConsoleTopBar } from "./ConsoleTopBar";
import {
  ConsoleSevenLayerPanel,
  type LayerRow,
} from "./ConsoleSevenLayerPanel";

export interface ConsoleShellProps {
  fileName?: string;
  fileSize?: string;
  documentTitle?: string;
  documentType?: string;
  phase: ConsolePhase;
  run: PipelineRun;
  result?: ValidateFileResult | null;
  errorMessage?: string | null;
  core: React.ReactNode;
  variantLabel?: string;
  onCancel?: () => void;
  onBack?: () => void;
  topBarExtra?: React.ReactNode;
  notice?: { title: string; detail?: string } | null;
  footer?: React.ReactNode;
  onMinimize?: () => void;
  inline?: boolean;
  pipelineStageEvents?: Record<string, IngestStageEvent>;
  layerRuns?: Partial<Record<LayerKey, LayerRunState>>;
  domainScopeKey?: string;
  onRuleCountsChange?: (value: {
    documentId: string | null;
    counts: ConsoleRuleCounts | null;
  }) => void;
}

export function ConsoleShell({
  fileName,
  fileSize,
  documentTitle,
  documentType,
  phase,
  run,
  result = null,
  errorMessage = null,
  core,
  variantLabel,
  onCancel,
  onBack,
  topBarExtra,
  notice = null,
  footer,
  onMinimize,
  inline = false,
  pipelineStageEvents,
  layerRuns,
  domainScopeKey: seedScopeKey,
  onRuleCountsChange,
}: ConsoleShellProps) {
  const [openDrawer, setOpenDrawer] = React.useState<LayerKey | null>(null);
  const stages = PIPELINE_STAGES;
  const active = stages[Math.min(run.stage, stages.length - 1)];
  const domainResult = layerRuns?.domain?.result as
    | DomainClassifyResult
    | undefined;
  const documentId = result?.document?.id ?? domainResult?.document?.id ?? null;

  const domainScopeKey = seedScopeKey ?? null;

  const domainCounts = useDomainRuleCounts(documentId, domainScopeKey);
  const yields = buildYield(result, phase, domainCounts);

  React.useEffect(() => {
    onRuleCountsChange?.({ documentId, counts: domainCounts });
  }, [documentId, domainCounts, onRuleCountsChange]);

  const liveStageStates = buildStageStatesFromEvents(pipelineStageEvents);
  const apiStageStates = liveStageStates ?? buildStageStates(result);


  const allLayersComplete = React.useMemo(
    () =>
      !!layerRuns &&
      VALIDATION_LAYERS.every(
        (l) =>
          layerRuns[l.key as LayerKey]?.status === LAYER_RUN_STATUS.COMPLETE,
      ),
    [layerRuns],
  );

  const stageState = (i: number): ConsoleStageState => {
    const apiState = apiStageStates?.[i];
    const isPostValidate = i > HOLD_STAGE_INDEX;
    if (allLayersComplete && isPostValidate && apiState !== "failed") {
      return "done";
    }
    return (
      apiState ??
      (run.done || i < run.stage
        ? "done"
        : i === run.stage
          ? "active"
          : "pending")
    );
  };

  const failedStageIndex = React.useMemo(() => {
    if (phase !== JOB_PHASE.ERROR && !apiStageStates) return -1;
    if (apiStageStates) {
      const idx = apiStageStates.findIndex((s) => s === "failed");
      if (idx >= 0) return idx;
    }
    if (phase === JOB_PHASE.ERROR) {
      return Math.min(run.stage, stages.length - 1);
    }
    return -1;
  }, [apiStageStates, phase, run.stage, stages.length]);

  const [failurePop, setFailurePop] = React.useState<
    { index: number; label: string; visible: boolean } | null
  >(null);
  const lastFailureRef = React.useRef<number>(-1);
  React.useEffect(() => {
    if (failedStageIndex < 0) {
      lastFailureRef.current = -1;
      setFailurePop(null);
      return;
    }
    if (lastFailureRef.current === failedStageIndex) return;
    lastFailureRef.current = failedStageIndex;
    const stage = stages[failedStageIndex];
    setFailurePop({ index: failedStageIndex, label: stage.label, visible: true });
    const hideAt = window.setTimeout(
      () => setFailurePop((p) => (p ? { ...p, visible: false } : null)),
      3500,
    );
    const removeAt = window.setTimeout(() => setFailurePop(null), 3900);
    return () => {
      window.clearTimeout(hideAt);
      window.clearTimeout(removeAt);
    };
  }, [failedStageIndex, stages]);

  const sevenLayer = result?.sevenLayerValidation ?? null;
  const apiLayers = (sevenLayer?.layers ?? []) as any[];
  const clearedCount = sevenLayer?.clearedLayers ?? 0;
  const layersSettled = !!result;
  const layersErrored = phase === JOB_PHASE.ERROR && !result;

  const layerRows: LayerRow[] = VALIDATION_LAYERS.map((def, i) => {
    const entryByKey = apiLayers.find((l: any) => l?.key === def.key);
    const entry = entryByKey ?? apiLayers[i];
    const evaluated = !!entry;
    const cleared = evaluated
      ? (entry.passed ??
        String(entry.status ?? "").toLowerCase() === "cleared")
      : i < clearedCount;

    return {
      key: def.key,
      label: def.label,
      icon: def.icon,
      cleared: evaluated && cleared,
      evaluated,
    };
  });

  const layersTotal = sevenLayer?.totalLayers ?? VALIDATION_LAYERS.length;
  const layersCleared = layerRows.filter((r) => r.cleared).length;
  const layersNotEvaluated = layerRows.filter(
    (r) => layersSettled && !r.evaluated,
  ).length;

  const jobStatus = String(result?.processingJob?.status ?? "").toUpperCase();
  const jobFailed = jobStatus === "FAILED" || jobStatus === "ERROR";
  const jobPartial = jobStatus === "PARTIAL";
  const allPassed =
    !!result && result.summary.fail === 0 && !jobFailed && !jobPartial;

  const statusKey: keyof typeof STATUS_TONE =
    phase === JOB_PHASE.ERROR || jobFailed
      ? "fail"
      : phase === JOB_PHASE.VALIDATING_LAYERS
        ? "running"
        : run.done && result
          ? allPassed
            ? "pass"
            : "warn"
          : "running";

  const statusLabel =
    phase === JOB_PHASE.ERROR || jobFailed
      ? "Validation failed"
      : phase === JOB_PHASE.VALIDATING_LAYERS
        ? "Validating layers"
        : run.done && result
          ? allPassed
            ? "All rules passed"
            : "Needs review"
          : active.label;

  const tone = STATUS_TONE[statusKey];

  const body = (
    <div
      className={`flex min-h-0 flex-col text-foreground ${inline
        ? "relative min-h-[640px] w-full min-w-0 flex-1 overflow-hidden rounded-2xl border border-border bg-background"
        : "relative min-h-[620px] w-full max-w-[1400px] overflow-hidden rounded-2xl border border-border bg-background shadow-2xl"
        }`}
      role={inline ? "region" : "dialog"}
      aria-modal={inline ? undefined : true}
      aria-label="Knowledge processing console"
    >
      <ConsoleKeyframes />

      <ConsoleTopBar
        variantLabel={variantLabel}
        statusLabel={statusLabel}
        statusTone={tone}
        run={run}
        notice={notice}
        topBarExtra={topBarExtra}
        onMinimize={onMinimize}
        onBack={onBack}
        onCancel={onCancel}
        footer={footer}
      />

      <div className="relative z-10 grid min-h-0 flex-1 gap-4 p-4 sm:p-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,320px)] xl:grid-cols-[minmax(0,1fr)_minmax(0,360px)]">
        <div className="flex min-h-0 flex-col">
          <Panel grow noHeader>
            <div className="flex flex-wrap items-center gap-3 px-4 pb-2.5 pt-2.5">
              <span className="grid size-10 flex-none place-items-center rounded-lg border border-border bg-[#f8fafc] text-indigo-500 dark:bg-white/[0.03] dark:text-indigo-400">
                <FileText className="size-5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="break-words text-sm font-bold leading-tight">
                  {documentTitle ||
                    result?.document?.title ||
                    fileName ||
                    result?.filename ||
                    "Untitled document"}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 break-words text-xs font-medium text-muted-foreground">
                  {(() => {
                    const rawDocType =
                      documentType ||
                      result?.document?.documentType ||
                      result?.docType;
                    if (!rawDocType) return null;
                    const key = String(rawDocType).toUpperCase();
                    return (
                      <span
                        className={`inline-flex items-center justify-center px-2 py-0.5 rounded-[4px] text-[10px] font-bold ${DOC_TYPE_COLORS[key] ?? "bg-[#f1f5f9] dark:bg-white/[0.05] text-[#475569] dark:text-white/60"}`}
                      >
                        {formatDocType(key)}
                      </span>
                    );
                  })()}
                  <span>
                    {[
                      fileName || result?.document?.fileName || result?.filename,
                      fileSize || result?.document?.formattedFileSize,
                      result?.processingJob?.formattedDuration,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "No file attached"}
                  </span>
                </div>
              </div>
            </div>
            <div className="h-px bg-border" />

            <ConsolePipelineStrip
              run={run}
              stageState={stageState}
              apiStageStates={apiStageStates}
              failurePop={failurePop}
            />
            <div className="h-px bg-border" />

            <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,280px)]">
              <div className="relative grid min-h-[340px] place-items-center overflow-hidden lg:min-h-[340px]">
                {core}
              </div>
              <div className="flex min-h-0 flex-col border-t border-border lg:max-h-[420px] lg:border-l lg:border-t-0">
                <div className="flex items-center gap-2 px-4 pb-2 pt-3 text-[11px] tracking-wide text-muted-foreground">
                  <span className="size-1.5 rounded-full bg-indigo-500" />
                  Validation Feed
                </div>
                <ExtractionFeed
                  result={result}
                  run={run}
                  phase={phase}
                  errorMessage={errorMessage}
                />
              </div>
            </div>
          </Panel>
        </div>

        <div className="flex min-h-0 flex-col gap-3">
          <Panel title="Validation Summary">
            <div className="grid grid-cols-2 gap-2 p-2.5">
              {yields.map((k, i) => {
                const isCompliance = k.label === "Compliance";
                const passedMatch = isCompliance
                  ? k.hint.match(/^(\d[\d,]*)(\s+of\s+.*)$/)
                  : null;
                const stretch =
                  yields.length % 2 === 1 && i === yields.length - 1
                    ? " col-span-2"
                    : "";
                return (
                  <div
                    key={k.label}
                    className={`flex min-w-0 flex-col rounded-lg border border-border bg-[#f8fafc] p-2.5 dark:bg-white/[0.02]${stretch}`}
                  >
                    <div className="mb-1 break-words text-[10.5px] font-medium leading-tight tracking-wide text-muted-foreground">
                      {k.label}
                    </div>
                    <div
                      className={`break-words text-2xl font-bold leading-none tracking-tight xl:text-3xl ${YIELD_TONE_CLASS[k.tone]}`}
                    >
                      {k.value}
                    </div>
                    {k.hint &&
                      (passedMatch ? (
                        <div className="mt-1 break-words text-[11px] leading-snug text-muted-foreground">
                          <span
                            className={`font-bold ${YIELD_TONE_CLASS[k.tone]}`}
                          >
                            {passedMatch[1]}
                          </span>
                          {passedMatch[2]}
                        </div>
                      ) : (
                        <div className="mt-1 break-words text-[10px] leading-snug text-muted-foreground">
                          {k.hint}
                        </div>
                      ))}
                  </div>
                );
              })}
            </div>
          </Panel>

          <Panel
            title="Seven-Layer Validation"
            // meta={`${layersCleared} / ${layersTotal}`}
            grow
          >
            <ConsoleSevenLayerPanel
              layerRows={layerRows}
              layerRuns={layerRuns}
              layersSettled={layersSettled}
              layersErrored={layersErrored}
              layersNotEvaluated={layersNotEvaluated}
              layersTotal={layersTotal}
              onOpenDrawer={setOpenDrawer}
            />

            {sevenLayer && !!sevenLayer.crossCuttingRules && (
              <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
                <span>{sevenLayer.crossCuttingRules} cross-cutting rules</span>
              </div>
            )}
          </Panel>
        </div>
      </div>

      {footer && (
        <div className="relative z-10 flex flex-shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border bg-card px-4 py-3 sm:px-5">
          {footer}
        </div>
      )}

      <DomainIntelligenceDrawer
        open={openDrawer === "domain"}
        onClose={() => setOpenDrawer(null)}
        result={domainResult ?? null}
        documentTitle={documentTitle}
        documentType={documentType}
        scopeKey={domainScopeKey}
      />
      <ConceptIntelligenceDrawer
        open={openDrawer === "concept"}
        onClose={() => setOpenDrawer(null)}
        result={
          (layerRuns?.concept?.result as ConceptClassifyResult | undefined) ??
          null
        }
        documentTitle={documentTitle}
        documentType={documentType}
      />
      <RequirementIntelligenceDrawer
        open={openDrawer === "requirement"}
        onClose={() => setOpenDrawer(null)}
        result={
          (layerRuns?.requirement?.result as
            | RequirementClassifyResult
            | undefined) ?? null
        }
        documentTitle={documentTitle}
        documentType={documentType}
      />
      <RelationshipIntelligenceDrawer
        open={openDrawer === "relationship"}
        onClose={() => setOpenDrawer(null)}
        result={
          (layerRuns?.relationship?.result as
            | RelationshipResult
            | undefined) ?? null
        }
        documentTitle={documentTitle}
        documentType={documentType}
      />
      <KnowledgeQualityDrawer
        open={openDrawer === "quality"}
        onClose={() => setOpenDrawer(null)}
        result={
          (layerRuns?.quality?.result as
            | KnowledgeQualityResult
            | undefined) ?? null
        }
        documentTitle={documentTitle}
        documentType={documentType}
      />
      <ConsistencyDrawer
        open={openDrawer === "consistency"}
        onClose={() => setOpenDrawer(null)}
        result={
          (layerRuns?.consistency?.result as ConsistencyResult | undefined) ??
          null
        }
        documentTitle={documentTitle}
        documentType={documentType}
      />
      <GovernanceDrawer
        open={openDrawer === "governance"}
        onClose={() => setOpenDrawer(null)}
        result={
          (layerRuns?.governance?.result as GovernanceResult | undefined) ??
          null
        }
        documentTitle={documentTitle}
        documentType={documentType}
      />
    </div>
  );

  if (inline) return body;

  return (
    <div className="fixed inset-y-0 left-0 right-[var(--companion-rail,0px)] z-[70] overflow-y-auto p-3 sm:p-6">
      <div
        className="fixed inset-y-0 left-0 right-[var(--companion-rail,0px)] bg-background/80 backdrop-blur-sm"
        onClick={run.done ? onCancel : undefined}
      />
      <div className="relative flex min-h-full items-center justify-center">
        {body}
      </div>
    </div>
  );
}

/**
 * Pass/fail split behind the Validation Summary, read from the domain layer's
 * findings endpoint — the same one the Domain Intelligence drawer uses for its
 */
function useDomainRuleCounts(
  documentId: string | null,
  scopeKey: string | null,
): ConsoleRuleCounts | null {

  const [state, setState] = React.useState<{
    documentId: string;
    scopeKey: string;
    counts: ConsoleRuleCounts | null;
  } | null>(null);

  React.useEffect(() => {
    if (!documentId) return;
    const controller = new AbortController();
    let alive = true;
    getDomainRuleValidation(
      documentId,
      scopeKey?.trim() || DOMAIN_RULE_VALIDATION_SCOPE_KEY,
      DOMAIN_RULE_VALIDATION_LAYER,
      DOMAIN_RULE_VALIDATION_OUTCOME,
      controller.signal,
    )
      .then((payload) => {
        if (!alive) return;
        const rows = toRuleValidationList(payload);
        const passed = rows.filter((rule) => ruleFindings(rule).length > 0).length;
        setState({
          documentId,
          scopeKey: scopeKey ?? DOMAIN_RULE_VALIDATION_SCOPE_KEY,
          counts: { total: rows.length, passed, failed: rows.length - passed },
        });
      })
      .catch(() => {
        /* Keep the placeholders rather than a stale document's numbers. */
        if (alive) {
          setState({
            documentId,
            scopeKey: scopeKey ?? DOMAIN_RULE_VALIDATION_SCOPE_KEY,
            counts: null,
          });
        }
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [documentId, scopeKey]);

  return state?.documentId === documentId &&
    state?.scopeKey === (scopeKey ?? DOMAIN_RULE_VALIDATION_SCOPE_KEY)
    ? state.counts
    : null;
}
